"use client";

import {
  isTypeBanner,
  TypeModalWithoutUnresolvableLinksResponse,
  TypeProgram_variationWithoutUnresolvableLinksResponse,
  TypeSchoolWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import { useContentfulLiveUpdates } from "@contentful/live-preview/react";
import React from "react";

// Direct imports for live preview support
import { AutomatedContentBanner } from "@2u-landing-pages/components/AutomatedContent";
import { Accordion } from "@2u-landing-pages/templates/Accordion";
import { Banner } from "@2u-landing-pages/templates/Banner";
import { Flex } from "@2u-landing-pages/templates/Flex";
import { Form } from "@2u-landing-pages/templates/Form";
import { DynamicExperienceFlow } from "@2u-landing-pages/templates/DynamicExperienceFlow";
import { Split } from "@2u-landing-pages/templates/Split";

interface ModalContentProps {
  modal: TypeModalWithoutUnresolvableLinksResponse;
  school: TypeSchoolWithoutUnresolvableLinksResponse;
  phoneNumber: string;
  programLogo: TypeProgram_variationWithoutUnresolvableLinksResponse["fields"]["logo_primary"];
  inspector: (props: { entryId: string; fieldId: string }) => any;
  isLoading: boolean;
  mode: "multi-program" | "single-program";
}
// @ts-ignore
import styles from "./Modal.module.scss";

export const ModalContent: React.FC<ModalContentProps> = ({
  modal,
  school,
  phoneNumber,
  programLogo,
  inspector,
  isLoading,
  mode,
}) => {
  // Use Contentful Live Updates hook to get real-time updates
  const updatedModal = useContentfulLiveUpdates(modal);
  const { content_slots, modal_padding } = updatedModal.fields;

  if (!content_slots || content_slots.length === 0) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <p>No content available for this modal.</p>
      </div>
    );
  }

  const renderContentSlot = (slot: any, index: number) => {
    if (!slot || !slot.sys?.contentType?.sys?.id) return null;

    const contentType = slot.sys.contentType.sys.id;
    const id = slot.sys.id;

    const style = {} as React.CSSProperties;
    if (isTypeBanner(slot) && slot?.fields?.sticky) {
      style.position = "sticky";
      style.zIndex = 100;
    }

    const commonProps = {
      id,
      school,
      sectionName: `Modal Content ${index + 1}`,
      phoneNumber,
      programLogo,
      inspector,
      isLoading,
      mode,
      style,
      location: "Modal",
    };

    const slotKey = `${contentType}-${id}`;

    switch (contentType) {
      case "accordion":
        return <Accordion key={slotKey} {...slot.fields} {...commonProps} />;
      case "automated_content":
        return <AutomatedContentBanner key={slotKey} {...slot.fields} {...commonProps} />;
      case "banner":
        return <Banner key={slotKey} {...slot.fields} {...commonProps} />;
      case "flex":
        return <Flex key={slotKey} {...slot.fields} {...commonProps} />;
      case "form":
        return <Form key={slotKey} {...slot.fields} {...commonProps} />;
      case "split":
        return <Split key={slotKey} {...slot.fields} {...commonProps} />;
      case "dynamic_experience_flow":
        return <DynamicExperienceFlow key={slotKey} {...slot.fields} {...commonProps} />;
      default:
        console.warn(`Unknown content type in modal: ${contentType}`);
        return null;
    }
  };

  return (
    <div
      className={styles.modalWrapper}
      style={
        {
          "--modal-padding-desktop": modal_padding ? "2rem" : "0",
          "--modal-padding-mobile": modal_padding ? "1rem" : "0",
          "--content-border-radius": modal_padding ? "1rem" : "0",
          "--sticky-top-desktop": modal_padding ? "2rem" : "0",
          "--sticky-top-mobile": modal_padding ? "1rem" : "0",
        } as React.CSSProperties
      }
    >
      <div className={styles.modalContentContainer}>
        <div className={styles.dialogScrollContainer}>
          <div className={styles.dialogContent}>
            {content_slots.map((slot, index) => renderContentSlot(slot, index))}
          </div>
        </div>
      </div>
    </div>
  );
};
